import {
  handoverMoments,
  handoverProgress,
  holdsPlace,
  isExternal,
  type HandoverKind,
  type HandoverProgress,
} from '@reditelji/shared';
import { checkinsByShift, liveViewsByShift, seenFrom } from '../live/derive';
import type { PoolVolunteer, Shift, TimelinePayload, TimelinePost } from '../api/types';
import { blockStatus } from '../timeline/derive';

/**
 * Predaje screen derivation, over the moments `handoverMoments` finds per post:
 * a predaja from whoever leaves to whoever arrives, a prevzem opening a post, a
 * zaključek closing it, a nadaljevanje where the same person carries on.
 * Whether one has happened is read from check-ins alone (`handoverProgress`).
 */

export interface HoEvent {
  id: string;
  kind: HandoverKind;
  at: number;
  post: TimelinePost;
  /** Who leaves — their shift ends here. */
  outs: Shift[];
  /** Who arrives — their shift starts here. */
  ins: Shift[];
  /** Latest outgoing end − earliest incoming start: >0 overlap, <0 a gap. */
  lap: number | null;
  /** Somebody else holds the post through it, so a gap leaves nobody without cover. */
  covered: boolean;
  warns: string[];
  sev: 'crit' | 'warn' | 'ok';
  crowd: number;
  /** Everyone arriving has checked in (a closing: everyone leaving has checked out). */
  done: boolean;
  progress: HandoverProgress;
}

export const BUCKET = 30;

export type HoStatus = 'potrjen' | 'čaka' | 'zavrnjen' | 'open' | 'done' | 'zunanja';
type StatusOf = (s: Shift) => HoStatus;

/**
 * What stands on one shift before the day: its own answer (SPEC §1.1F), for
 * every login — a refusal of Friday's shift says nothing about Saturday's.
 */
export function statusOfFactory(volunteers: PoolVolunteer[]): StatusOf {
  const byId = new Map(volunteers.map((v) => [v.id, v]));
  return (s: Shift) => {
    if (s.state === 'done') return 'done';
    if (!holdsPlace(s)) return 'open';
    const v = byId.get(s.volunteerId!);
    // A holder off the roster can only be keeping a started shift: settled,
    // as the heatmap reads it, with nobody to chase.
    if (!v) return 'potrjen';
    // The board's own reading of the shift (`blockStatus`), in Predaje's words.
    // `prosto` cannot come back: the slot is held and its holder known.
    switch (blockStatus(s, v)) {
      case 'zunanja':
        return 'zunanja';
      case 'potrjen':
      case 'opravljeno':
        return 'potrjen';
      case 'zavrnjen':
        return 'zavrnjen';
      default:
        return 'čaka';
    }
  };
}

/**
 * Nothing more is owed on this shift before the handover.
 *
 * Asked here rather than at each warning, so a status that is settled for its
 * own reason — an external team never confirms with us — cannot be read as
 * "still waiting" by a test that only knows two settled values.
 */
export const settled = (st: HoStatus): boolean =>
  st === 'potrjen' || st === 'done' || st === 'zunanja';

export function deriveHoEvents(data: TimelinePayload, now: number): HoEvent[] {
  const statusOf = statusOfFactory(data.volunteers);
  const cm = checkinsByShift(data.checkins);
  const external = new Set(data.volunteers.filter(isExternal).map((v) => v.id));
  const postsById = new Map(data.posts.map((p) => [p.id, p]));
  // An arrival counts for the whole stretch of back-to-back shifts, as on the map.
  const seen = seenFrom(cm, external, liveViewsByShift(data.posts, cm, now));

  const evs: Omit<HoEvent, 'crowd'>[] = handoverMoments(data.posts.flatMap((p) => p.shifts)).map((m) => {
    const p = postsById.get(m.postId)!;
    const { kind, outs, ins, lap } = m;
    const at = m.atMin;
    const staffed = (s: Shift) => holdsPlace(s);
    const warns: string[] = [];
    // Somebody staying through it keeps the post held, whatever the lap; one
    // person's own break at a post nobody else holds leaves it empty too.
    if (lap !== null && !m.covered) {
      if (lap < 0) {
        warns.push(`Mesto je ${-lap} min brez redarja — odhajajoči odide pred prihodom zamenjave.`);
      } else if (lap === 0 && p.urgent && kind === 'predaja') {
        warns.push('Nujno mesto brez prekrivanja — če zamenjava zamudi, mesto ostane prazno.');
      }
    }
    const inSt = ins.map(statusOf);
    if (kind === 'predaja') {
      if (ins.some((s) => !staffed(s))) warns.push('Zamenjava ni določena.');
      if (inSt.includes('zavrnjen')) warns.push('Zamenjava je odpovedala.');
      else if (ins.some((s, i) => staffed(s) && !settled(inSt[i]!))) warns.push('Zamenjava še ni potrdila prihoda.');
      if (outs.some((s) => !staffed(s))) warns.push('Prejšnja izmena je bila nepokrita.');
      if (at >= 1260 || at < 360) warns.push('Predaja v temi — poskrbi za odsevnike in svetilko.');
    } else if (kind === 'prevzem') {
      if (ins.some((s) => !staffed(s))) warns.push('Prva izmena na tem mestu ni zasedena.');
      else if (inSt.some((st) => !settled(st))) warns.push('Prva izmena še ni potrjena.');
    } else if (kind === 'zaključek' && outs.some((s) => !staffed(s))) {
      warns.push('Zadnja izmena ni bila zasedena.');
    }

    const unstaffedIn = ins.some((s) => !staffed(s));
    const sev: HoEvent['sev'] =
      (lap !== null && lap < 0 && (kind === 'predaja' || kind === 'nadaljevanje') && !m.covered) ||
      ((kind === 'predaja' || kind === 'prevzem') && unstaffedIn) ||
      (kind === 'predaja' && inSt.includes('zavrnjen'))
        ? 'crit'
        : ins.some((s, i) => staffed(s) && !settled(inSt[i]!) && inSt[i] !== 'zavrnjen') ||
            (lap === 0 && p.urgent && kind === 'predaja' && !m.covered)
          ? 'warn'
          : 'ok';

    const progress = handoverProgress(m, seen, now);
    return { id: m.id, kind, at, post: p, outs, ins, lap, covered: m.covered, warns, sev, done: progress.complete, progress };
  });

  // crowding: 30-min buckets over predaja events
  const bcount = new Map<number, number>();
  for (const e of evs) {
    if (e.kind !== 'predaja') continue;
    const b = Math.floor(e.at / BUCKET) * BUCKET;
    bcount.set(b, (bcount.get(b) ?? 0) + 1);
  }
  return evs.map((e) => ({
    ...e,
    crowd: e.kind === 'predaja' ? (bcount.get(Math.floor(e.at / BUCKET) * BUCKET) ?? 0) : 0,
  }));
}

/** Per-30-min histogram severity (worst predaja in the bucket). */
export function bucketSeverity(evs: HoEvent[]): Map<number, 'crit' | 'warn' | 'ok'> {
  const map = new Map<number, 'crit' | 'warn' | 'ok'>();
  for (const e of evs) {
    if (e.kind !== 'predaja') continue;
    const b = Math.floor(e.at / BUCKET) * BUCKET;
    const cur = map.get(b);
    if (e.sev === 'crit' || cur === 'crit') map.set(b, 'crit');
    else if (e.sev === 'warn' || cur === 'warn') map.set(b, 'warn');
    else map.set(b, cur ?? 'ok');
  }
  return map;
}

export const SEV_STYLE = {
  crit: { bg: 'oklch(0.988 0.018 25)', bd: 'oklch(0.88 0.05 25)', dot: 'oklch(0.57 0.14 25)', fg: 'oklch(0.44 0.12 25)', bs: 'solid' },
  warn: { bg: 'oklch(0.995 0.012 75)', bd: 'oklch(0.89 0.055 75)', dot: 'oklch(0.68 0.11 75)', fg: 'oklch(0.45 0.09 75)', bs: 'dashed' },
  ok: { bg: 'oklch(1 0 0)', bd: 'oklch(0.94 0.006 95)', dot: 'oklch(0.62 0.09 155)', fg: 'oklch(0.36 0.07 155)', bs: 'solid' },
} as const;

export const KIND_STYLE: Record<HandoverKind, { label: string; fg: string }> = {
  prevzem: { label: 'PREVZEM', fg: 'oklch(0.5 0.07 245)' },
  predaja: { label: 'PREDAJA', fg: 'oklch(0.45 0.012 90)' },
  nadaljevanje: { label: 'NADALJUJE', fg: 'oklch(0.55 0.012 90)' },
  'zaključek': { label: 'ZAKLJUČEK', fg: 'oklch(0.55 0.012 90)' },
};

export function lapText(e: HoEvent): string {
  if (e.kind === 'prevzem') return 'odprtje mesta';
  if (e.kind === 'zaključek') return 'zaprtje mesta';
  if (e.kind === 'nadaljevanje') return 'isti redar nadaljuje';
  if (e.covered) return 'mesto ostane pokrito';
  if (e.lap !== null && e.lap > 0) return `prekrivanje ${e.lap} min`;
  if (e.lap === 0) return e.post.urgent ? 'brez prekrivanja — nujno mesto' : 'iz minute v minuto';
  return `vrzel ${-(e.lap ?? 0)} min`;
}

export function lapStyle(e: HoEvent): { bg: string; fg: string } {
  if (e.kind === 'prevzem' || e.kind === 'zaključek' || e.kind === 'nadaljevanje' || e.covered) {
    return { bg: 'oklch(0.968 0.005 95)', fg: 'oklch(0.45 0.012 90)' };
  }
  if (e.lap !== null && e.lap > 0) return { bg: 'oklch(0.95 0.035 155)', fg: 'oklch(0.34 0.06 155)' };
  if (e.lap === 0) {
    return e.post.urgent
      ? { bg: 'oklch(0.965 0.035 75)', fg: 'oklch(0.44 0.09 75)' }
      : { bg: 'oklch(0.968 0.005 95)', fg: 'oklch(0.45 0.012 90)' };
  }
  return { bg: 'oklch(0.95 0.04 25)', fg: 'oklch(0.44 0.12 25)' };
}
